import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import MapIcon from '@mui/icons-material/Map';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAppTheme from 'hooks/useAppTheme';
import { useSortableRows, type SortValueGetters } from 'hooks/useSortableRows';
import { SortableTh } from 'components/Table/SortableTh';
import { useAppDispatch } from 'store/hook';
import { showNotification } from 'store/notification.slice';
import ManualDispatchAPI, {
  type ManualDispatchAssignment,
  type ManualDispatchExcavatorInput,
} from 'api/manual-dispatch.api';
import { type GradeRecord } from 'views/DispatchImporters/GradeImporterView';
import {
  type AssetInfo,
  type LastStatus,
  type DumpOption,
  type ManualExcMeta,
  MultiSelectCell,
  getTruckStatusDisplay,
} from './DispatchPlanV2View';

interface ManualExcavatorPanelProps {
  excavators: AssetInfo[];
  haulTrucks: AssetInfo[];
  lastStatuses: Record<string, LastStatus>;
  grades: GradeRecord[];
  gradesByLocation: Record<string, GradeRecord[]>;
  dumpOptions: DumpOption[];
  statusGroups: Map<string, { colorCode: string; reasons: string[] }>;
  resolveReasonDisplay: (
    status: string | null | undefined,
    reason: string | null | undefined,
    description?: string | null
  ) => string | null;
  manualMeta: Record<string, ManualExcMeta>;
  setManualMeta: React.Dispatch<
    React.SetStateAction<Record<string, ManualExcMeta>>
  >;
  onOpenStatusModal: (exc: AssetInfo) => void;
  onOpenMap: (exc: AssetInfo) => void;
  onToggleHardLock: (exc: AssetInfo, checked: boolean) => Promise<void>;
  onCapacityChange: (exc: AssetInfo, capacity: string) => Promise<void>;
  onSaveManualConfig: (
    exc: AssetInfo,
    patch: Partial<
      Pick<
        ManualExcMeta,
        'primaryGradeBlock' | 'dumpingZoneNames' | 'lockedTrucks'
      >
    >
  ) => Promise<void>;
  onRefresh: () => Promise<void>;
  isRefreshing: boolean;
}

const EMPTY_META: ManualExcMeta = {
  primaryGradeBlock: '',
  dumpingZoneNames: [],
  lockedTrucks: [],
  needsAllocation: false,
  capacity: '',
  hardlock: false,
};

const STATUS_DISPLAY_NAMES: Record<string, string> = { active: 'READY' };

// Raw status values that display as "READY" — activity/reason text is only
// meaningful (and only shown) while an asset is in one of these states.
const isReadyStatus = (statusRaw?: string | null): boolean => {
  const rawKey = statusRaw?.toLowerCase() ?? '';
  return rawKey === 'active' || rawKey === 'ready';
};

const ManualExcavatorPanel: React.FC<ManualExcavatorPanelProps> = ({
  excavators,
  haulTrucks,
  lastStatuses,
  grades,
  gradesByLocation,
  dumpOptions,
  statusGroups,
  resolveReasonDisplay,
  manualMeta,
  setManualMeta,
  onOpenStatusModal,
  onOpenMap,
  onToggleHardLock,
  onCapacityChange,
  onSaveManualConfig,
  onRefresh,
  isRefreshing,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { enableDarkTheme } = useAppTheme();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const manualDispatchApi = useRef(new ManualDispatchAPI()).current;
  // whether a plan already exists — once true, "Dispatch Plan" only PATCHes
  // the excavators the dispatcher actually edited instead of resubmitting everything
  const [hasActivePlan, setHasActivePlan] = useState(false);
  // fields the dispatcher has edited since the last successful save, per excavator —
  // only these get sent when doing an incremental (PATCH) submit
  const [manualDirty, setManualDirty] = useState<
    Record<string, Partial<ManualExcMeta>>
  >({});
  const hasUnsavedEdits = Object.keys(manualDirty).length > 0;
  // href the dispatcher tried to navigate to while a confirm-leave dialog is up
  const [pendingNavHref, setPendingNavHref] = useState<string | null>(null);
  // live assignments from the active plan — used only to flag when a locked truck's
  // actual dump location has drifted from the excavator's own configured dump zone(s)
  const [activeAssignments, setActiveAssignments] = useState<
    ManualDispatchAssignment[]
  >([]);

  // guard against losing unsaved excavator edits: native browser navigation
  // (refresh/close tab/typed URL) via beforeunload, and in-app sidebar/breadcrumb
  // navigation by intercepting anchor clicks in the capture phase — react-router's
  // classic BrowserRouter here has no useBlocker/usePrompt (those need a data
  // router), so this is the only way to intercept a same-app route change
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!hasUnsavedEdits) return;
      e.preventDefault();
      e.returnValue = '';
    };
    const handleClickCapture = (e: MouseEvent) => {
      if (!hasUnsavedEdits) return;
      if (e.defaultPrevented || e.button !== 0) return;
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = (e.target as HTMLElement)?.closest?.('a[href]');
      if (
        !(anchor instanceof HTMLAnchorElement) ||
        anchor.target === '_blank'
      ) {
        return;
      }
      const dest = new URL(anchor.href, window.location.origin);
      if (dest.pathname === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNavHref(dest.pathname + dest.search);
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('click', handleClickCapture, true);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('click', handleClickCapture, true);
    };
  }, [hasUnsavedEdits]);

  const rehydrateFromActivePlan = useCallback(async () => {
    const active = await manualDispatchApi.getActivePlan();
    setHasActivePlan(active !== null);
    setActiveAssignments(active?.assignments ?? []);
    if (!active) return;

    // one config row per excavator regardless of truck count (Backend Update #2) —
    // no more deriving grade/dump from truck assignments, so zero-locked-truck
    // excavators restore correctly too
    const nextMeta: Record<string, ManualExcMeta> = {};
    active.excavators.forEach((excConfig) => {
      const exc = excavators.find(
        (e) => e.bumperNumber === excConfig.excavatorBumper
      );
      if (!exc) return;
      const grade = grades.find((g) => g.id === excConfig.gradeId);
      const lockedTrucks = active.assignments
        .filter((a) => a.excavatorBumper === excConfig.excavatorBumper)
        .map((a) => a.truckBumper);
      nextMeta[exc.symbotId] = {
        primaryGradeBlock: grade?.name ?? '',
        dumpingZoneNames: excConfig.dumpingNodes ?? [],
        lockedTrucks,
        needsAllocation: excConfig.needsAllocation,
        capacity:
          excConfig.capacity != null
            ? String(excConfig.capacity)
            : String(exc.meta?.capacity ?? ''),
        hardlock: Boolean(exc.meta?.hardlock),
      };
    });
    setManualMeta(nextMeta);
  }, [manualDispatchApi, excavators, grades, setManualMeta]);

  // the parent already seeds manualMeta from the active plan as part of the
  // initial page load (before the table ever paints) — this just keeps
  // hasActivePlan fresh (e.g. after another dispatcher creates the first plan)
  // without waiting for the first 30s poll tick
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      const plan = await manualDispatchApi.getActivePlan();
      if (!cancelled) {
        setHasActivePlan(plan !== null);
        setActiveAssignments(plan?.assignments ?? []);
      }
    };
    void poll();
    const interval = setInterval(() => {
      void poll();
    }, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [manualDispatchApi]);

  const [truckPopOpen, setTruckPopOpen] = useState<string | null>(null);
  const [dumpPopOpen, setDumpPopOpen] = useState<string | null>(null);

  const [isDispatching, setIsDispatching] = useState(false);
  // AUTO PLAN disabled for now — manual mode stays fully manual, no solver calls.
  // Kept commented out (not deleted) in case auto-allocation comes back later.
  // const [allocationModal, setAllocationModal] = useState<{
  //   // 'full' = first-time plan creation (POST), 'incremental' = per-excavator PATCH
  //   mode: 'full' | 'incremental';
  //   // every excavator this submit will send — allExcavators superset the zero-locked
  //   // rows below, which only need a dispatcher choice
  //   allExcavators: AssetInfo[];
  //   rows: Array<{
  //     exc: AssetInfo;
  //     choice: 'pending' | 'auto' | 'save';
  //     capacity: string;
  //   }>;
  // } | null>(null);

  const panelBg = enableDarkTheme
    ? alpha(theme.palette.common.white, 0.04)
    : theme.palette.background.paper;
  const borderColor = enableDarkTheme
    ? alpha(theme.palette.common.white, 0.08)
    : theme.palette.divider;
  const textPrimary = theme.palette.text.primary;
  const textMuted = theme.palette.text.secondary;

  const tdBase: React.CSSProperties = {
    padding: '10px 12px',
    borderBottom: `1px solid ${borderColor}`,
    whiteSpace: 'nowrap',
    fontSize: 12,
  };
  const thBase: React.CSSProperties = {
    textAlign: 'left',
    padding: '10px 12px',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: textMuted,
    borderBottom: `1px solid ${borderColor}`,
    whiteSpace: 'nowrap',
    backgroundColor: panelBg,
  };

  // bumperNumber -> symbotId of the excavator that currently has it locked (manual mode) —
  // reflects unsaved edits too, so another row's picker excludes a truck the instant it's
  // tentatively locked here, before that edit is saved
  const truckLockOwner = useMemo(() => {
    const map: Record<string, string> = {};
    excavators.forEach((exc) => {
      const locked =
        manualDirty[exc.symbotId]?.lockedTrucks ??
        manualMeta[exc.symbotId]?.lockedTrucks ??
        [];
      locked.forEach((bumper) => {
        map[bumper] = exc.symbotId;
      });
    });
    return map;
  }, [excavators, manualMeta, manualDirty]);

  // effective value = last-saved baseline merged with any not-yet-submitted edits
  // the last-saved baseline, with no dirty edits applied — used to tell a real
  // change apart from a field that was touched then set back to its own value
  const getSavedMeta = (exc: AssetInfo): ManualExcMeta =>
    manualMeta[exc.symbotId] ?? {
      ...EMPTY_META,
      capacity: String(exc.meta?.capacity ?? ''),
      // don't inherit a hardlock left over on the asset from a previous plan —
      // with no active plan right now, there's nothing for it to be locking
      hardlock: hasActivePlan ? Boolean(exc.meta?.hardlock) : false,
    };

  const getMeta = (exc: AssetInfo): ManualExcMeta => ({
    ...getSavedMeta(exc),
    ...manualDirty[exc.symbotId],
  });

  const setMeta = <K extends keyof ManualExcMeta>(
    exc: AssetInfo,
    field: K,
    value: ManualExcMeta[K]
  ) => {
    setManualDirty((prev) => ({
      ...prev,
      [exc.symbotId]: { ...prev[exc.symbotId], [field]: value },
    }));
  };

  // Mirrors the same formulas the row cells below compute inline — kept as
  // a parallel set of getters (rather than refactoring the row render to
  // consume them) so sorting can't accidentally change what's displayed.
  const getRowNearbyBlocks = (exc: AssetInfo): GradeRecord[] => {
    const locationName = (exc.nearestLoading?.name ?? '').trim();
    return gradesByLocation[locationName.toLowerCase()] ?? [];
  };

  const excavatorSortGetters: SortValueGetters<AssetInfo> = {
    excavator: (exc) => exc.bumperNumber,
    capacity: (exc) => {
      const num = parseFloat(getMeta(exc).capacity);
      return isFinite(num) ? num : null;
    },
    operator: (exc) => {
      const name = lastStatuses[exc.symbotId]?.operatorName;
      return name && name.toLowerCase() !== 'system' ? name : null;
    },
    status: (exc) => lastStatuses[exc.symbotId]?.payload?.status ?? null,
    location: (exc) => {
      const name = (exc.nearestLoading?.name ?? '').trim();
      if (!name) return null;
      return name;
    },
    nearbyBlocks: (exc) => getRowNearbyBlocks(exc).length,
    grade: (exc) => {
      const value = getMeta(exc).primaryGradeBlock;
      if (!value) return null;
      return value;
    },
    materialGrade: (exc) => {
      const meta = getMeta(exc);
      const primaryBlock = getRowNearbyBlocks(exc).find(
        (g) =>
          g.name.trim().toLowerCase() ===
          meta.primaryGradeBlock.trim().toLowerCase()
      );
      return primaryBlock?.materialCategory ?? null;
    },
    lockedTrucks: (exc) => getMeta(exc).lockedTrucks.length,
    dump: (exc) => getMeta(exc).dumpingZoneNames.length,
    hardlock: (exc) => (getMeta(exc).hardlock ? 1 : 0),
  };

  const {
    sortedRows: sortedExcavators,
    sortKey: excSortKey,
    direction: excDirection,
    toggleSort: toggleExcSort,
  } = useSortableRows(excavators, excavatorSortGetters);

  const resolveGradeId = (exc: AssetInfo, gradeName: string): string => {
    const locationName = (exc.nearestLoading?.name ?? '').trim().toLowerCase();
    const nearby = gradesByLocation[locationName] ?? [];
    const grade = nearby.find(
      (g) => g.name.trim().toLowerCase() === gradeName.trim().toLowerCase()
    );
    return grade?.id ?? '';
  };

  const resolveDumpIds = (dumpNames: string[]): string[] =>
    dumpNames.flatMap((dumpName) => {
      const dump = dumpOptions.find(
        (d) => d.name.trim().toLowerCase() === dumpName.trim().toLowerCase()
      );
      return dump ? [dump.id] : [];
    });

  const readyExcavators = () =>
    excavators.filter(
      (exc) =>
        (lastStatuses[exc.symbotId]?.payload?.status ?? '').toLowerCase() !==
        'down'
    );

  const buildExcavatorInput = (
    exc: AssetInfo,
    overrides?: { needsAllocation: boolean; capacity: string }
  ): ManualDispatchExcavatorInput => {
    const meta = getMeta(exc);
    const needsAllocation = overrides?.needsAllocation ?? meta.needsAllocation;
    const capacity = overrides?.capacity ?? meta.capacity;
    return {
      excavatorBumper: exc.bumperNumber,
      gradeId: resolveGradeId(exc, meta.primaryGradeBlock),
      dumpingZoneIds: resolveDumpIds(meta.dumpingZoneNames),
      lockedTrucks: meta.lockedTrucks,
      needsAllocation,
      // always send capacity, regardless of needsAllocation — the field is
      // editable on every row and shouldn't be dropped just because this
      // excavator already has locked trucks
      capacity: Number(capacity) || 0,
    };
  };

  // first-time plan creation (no active plan yet) — full-plan replace, unchanged
  // from the original behavior: every ready excavator is sent every time.
  // Dispatch always wins now — no more Reload/Replace prompt on a 409; if
  // another dispatcher's plan raced ahead, immediately retry with
  // overridePlan so this dispatcher's current config becomes the plan.
  const submitFullPlan = async (
    resolvedZeroLocked: Record<
      string,
      { needsAllocation: boolean; capacity: string }
    >
  ) => {
    const excs = readyExcavators();
    const excavatorPayload = excs.map((exc) =>
      buildExcavatorInput(exc, resolvedZeroLocked[exc.symbotId])
    );
    const truckPayload = haulTrucks.map((tr) => ({
      bumperNumber: tr.bumperNumber,
    }));

    try {
      try {
        await manualDispatchApi.upsertPlan({
          status: 'active',
          overridePlan: false,
          excavators: excavatorPayload,
          trucks: truckPayload,
        });
      } catch (err: any) {
        if (err?.response?.status !== 409) throw err;
        await manualDispatchApi.upsertPlan({
          status: 'active',
          overridePlan: true,
          excavators: excavatorPayload,
          trucks: truckPayload,
        });
      }

      setManualMeta((prev) => {
        const next = { ...prev };
        excs.forEach((exc) => {
          next[exc.symbotId] = getMeta(exc);
        });
        return next;
      });
      setManualDirty({});
      setHasActivePlan(true);

      dispatch(
        showNotification({
          message: t('manual_dispatch_submit_success'),
          type: 'success',
        })
      );
    } catch (err: any) {
      const message =
        err?.response?.data?.data?.error?.detail ??
        err?.response?.data?.message ??
        t('manual_dispatch_submit_error');
      dispatch(showNotification({ message, type: 'error' }));
    }
  };

  // a plan already exists — batch-PATCH every dirty excavator in ONE atomic call
  // (Backend Update #9). The backend resolves every listed truck's fate from a
  // single snapshot in one transaction, so swapping two trucks between two
  // excavators can no longer race the way two independent single-excavator
  // PATCHes could. The write is all-or-nothing — a conflict fails the whole
  // batch with a single 409, never a partial application, so there's no more
  // per-excavator success/failure bookkeeping to do here.
  const submitDirtyExcavators = async (
    dirtyExcs: AssetInfo[],
    resolvedZeroLocked: Record<
      string,
      { needsAllocation: boolean; capacity: string }
    >
  ) => {
    try {
      const excavatorPayload = dirtyExcs.map((exc) =>
        buildExcavatorInput(exc, resolvedZeroLocked[exc.symbotId])
      );
      await manualDispatchApi.patchExcavators({
        excavators: excavatorPayload,
      });

      setManualMeta((prev) => {
        const next = { ...prev };
        dirtyExcs.forEach((exc) => {
          next[exc.symbotId] = getMeta(exc);
        });
        return next;
      });
      const dirtyIds = new Set(dirtyExcs.map((exc) => exc.symbotId));
      setManualDirty((prev) => {
        const next: Record<string, Partial<ManualExcMeta>> = {};
        Object.keys(prev).forEach((symbotId) => {
          if (!dirtyIds.has(symbotId)) next[symbotId] = prev[symbotId];
        });
        return next;
      });

      dispatch(
        showNotification({
          message: t('manual_dispatch_submit_success'),
          type: 'success',
        })
      );
      // refresh activeAssignments immediately — otherwise a truck just
      // self-heal-reassigned to a different excavator (Backend Update #8)
      // still shows the "reassigned elsewhere" warning until the next 30s
      // poll tick, even though it succeeded
      await rehydrateFromActivePlan();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        dispatch(
          showNotification({
            message: t('manual_dispatch_patch_conflict'),
            type: 'error',
          })
        );
        await rehydrateFromActivePlan();
      } else {
        dispatch(
          showNotification({
            message: t('manual_dispatch_submit_error'),
            type: 'error',
          })
        );
      }
    }
  };

  // Save Config no longer exists as its own action — Dispatch is now the
  // single button, and it always both durably persists every pending field
  // to asset meta (what Save used to do) AND pushes the current config into
  // the live plan, overwriting whatever's there. Only hardlock/capacity ever
  // needed a config-completeness bypass (they're asset-meta-only, never part
  // of the plan payload); grade/dump/lockedTrucks always required it.
  const persistManualConfig = async (dirtyExcs: AssetInfo[]) => {
    if (dirtyExcs.length === 0) return;
    const results = await Promise.allSettled(
      dirtyExcs.map(async (exc) => {
        const patch = manualDirty[exc.symbotId] ?? {};
        const saved = getSavedMeta(exc);
        // only actually persist if the field genuinely differs from what's
        // already saved — a field that was touched and set back to its own
        // value still shows up in manualDirty, but there's nothing to save
        if (patch.hardlock !== undefined && patch.hardlock !== saved.hardlock) {
          await onToggleHardLock(exc, patch.hardlock);
        }
        if (patch.capacity !== undefined && patch.capacity !== saved.capacity) {
          await onCapacityChange(exc, patch.capacity);
        }
        const configPatch: Partial<
          Pick<
            ManualExcMeta,
            'primaryGradeBlock' | 'dumpingZoneNames' | 'lockedTrucks'
          >
        > = {};
        if (
          patch.primaryGradeBlock !== undefined &&
          patch.primaryGradeBlock !== saved.primaryGradeBlock
        ) {
          configPatch.primaryGradeBlock = patch.primaryGradeBlock;
        }
        if (
          patch.dumpingZoneNames !== undefined &&
          patch.dumpingZoneNames.join(',') !== saved.dumpingZoneNames.join(',')
        ) {
          configPatch.dumpingZoneNames = patch.dumpingZoneNames;
        }
        if (
          patch.lockedTrucks !== undefined &&
          patch.lockedTrucks.join(',') !== saved.lockedTrucks.join(',')
        ) {
          configPatch.lockedTrucks = patch.lockedTrucks;
        }
        if (Object.keys(configPatch).length > 0) {
          await onSaveManualConfig(exc, configPatch);
        }
        return exc;
      })
    );

    const succeeded: AssetInfo[] = [];
    let hadError = false;
    results.forEach((r) => {
      if (r.status === 'fulfilled') {
        succeeded.push(r.value);
      } else {
        hadError = true;
      }
    });

    if (succeeded.length > 0) {
      setManualMeta((prev) => {
        const next = { ...prev };
        succeeded.forEach((exc) => {
          next[exc.symbotId] = getMeta(exc);
        });
        return next;
      });
      // fully resolved by this persist alone for any excavator the upcoming
      // plan submit won't touch (e.g. a DOWN excavator's hardlock-only edit,
      // since that submit only ever covers readyExcavators()) — clear it here
      setManualDirty((prev) => {
        const next: Record<string, Partial<ManualExcMeta>> = {};
        Object.entries(prev).forEach(([symbotId, patch]) => {
          if (!succeeded.some((exc) => exc.symbotId === symbotId)) {
            next[symbotId] = patch;
          }
        });
        return next;
      });
    }

    if (hadError) {
      dispatch(
        showNotification({
          message: t('manual_dispatch_config_save_error'),
          type: 'error',
        })
      );
    }
  };

  const handleDispatchClick = () => {
    if (!hasActivePlan) {
      const excs = readyExcavators();
      const missingConfig = excs.filter((exc) => {
        const meta = getMeta(exc);
        return (
          !meta.primaryGradeBlock ||
          meta.dumpingZoneNames.length === 0 ||
          // a picked grade name that doesn't resolve to a real gradeId for
          // this excavator's nearest loading location (stale/mismatched
          // selection) is just as invalid as never picking one — the backend
          // rejects an empty gradeId string outright, so catch it here first
          !resolveGradeId(exc, meta.primaryGradeBlock)
        );
      });
      if (missingConfig.length > 0) {
        dispatch(
          showNotification({
            message: t('dpv2_manual_ready_requires_config'),
            type: 'error',
          })
        );
        return;
      }

      void (async () => {
        setIsDispatching(true);
        try {
          await persistManualConfig(
            excavators.filter((exc) => manualDirty[exc.symbotId] !== undefined)
          );
          await submitFullPlan({});
        } finally {
          setIsDispatching(false);
        }
      })();
      return;
    }

    const dirtyExcs = readyExcavators().filter(
      (exc) => manualDirty[exc.symbotId] !== undefined
    );
    if (dirtyExcs.length === 0) {
      dispatch(
        showNotification({
          message: t('manual_dispatch_no_changes'),
          type: 'info',
        })
      );
      return;
    }

    const missingConfig = dirtyExcs.filter((exc) => {
      const meta = getMeta(exc);
      return (
        !meta.primaryGradeBlock ||
        meta.dumpingZoneNames.length === 0 ||
        !resolveGradeId(exc, meta.primaryGradeBlock)
      );
    });
    if (missingConfig.length > 0) {
      dispatch(
        showNotification({
          message: t('dpv2_manual_ready_requires_config'),
          type: 'error',
        })
      );
      return;
    }

    void (async () => {
      setIsDispatching(true);
      try {
        await persistManualConfig(
          excavators.filter((exc) => manualDirty[exc.symbotId] !== undefined)
        );
        await submitDirtyExcavators(dirtyExcs, {});
      } finally {
        setIsDispatching(false);
      }
    })();
  };

  const hasPendingManualEdits = readyExcavators().some(
    (exc) => manualDirty[exc.symbotId] !== undefined
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
        <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary }}>
          {t('dpv2_excavator_inputs')}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={t('dpv2_btn_refresh')}>
            <span>
              <IconButton
                onClick={() => {
                  void onRefresh();
                }}
                disabled={isRefreshing || isDispatching}
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: (th) => alpha(th.palette.divider, 0.6),
                  borderRadius: '8px',
                  p: 0.8,
                }}
              >
                <RefreshIcon
                  fontSize="small"
                  sx={{
                    transition: 'transform 0.5s',
                    transform: isRefreshing ? 'rotate(360deg)' : 'none',
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isDispatching ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )
            }
            disabled={
              isDispatching || (hasActivePlan && !hasPendingManualEdits)
            }
            onClick={handleDispatchClick}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          >
            {isDispatching ? t('dpv2_dispatching') : t('dpv2_dispatch_plan')}
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          bgcolor: panelBg,
          border: `1px solid ${borderColor}`,
          borderRadius: 2,
        }}
      >
        <Box sx={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 12.5,
            }}
          >
            <thead>
              <tr>
                {[
                  {
                    key: 'excavator',
                    label: t('manual_dispatch_col_excavator'),
                  },
                  { key: 'capacity', label: t('dpv2_col_capacity') },
                  { key: 'operator', label: t('dpv2_col_operator') },
                  { key: 'status', label: t('manual_dispatch_col_status') },
                  { key: 'location', label: t('dpv2_col_location') },
                  { key: 'nearbyBlocks', label: t('dpv2_col_nearby_blocks') },
                  { key: 'grade', label: t('manual_dispatch_col_grade') },
                  {
                    key: 'materialGrade',
                    label: t('dpv2_col_material_grade'),
                  },
                  {
                    key: 'lockedTrucks',
                    label: t('manual_dispatch_col_locked_trucks'),
                  },
                  { key: 'dump', label: t('manual_dispatch_col_dump') },
                ].map((col) => (
                  <SortableTh
                    key={col.key}
                    label={col.label}
                    sortKey={col.key}
                    activeSortKey={excSortKey}
                    direction={excDirection}
                    onSort={toggleExcSort}
                    style={thBase}
                  />
                ))}
                <SortableTh
                  label={t('dpv2_col_hardlock')}
                  sortKey="hardlock"
                  activeSortKey={excSortKey}
                  direction={excDirection}
                  onSort={toggleExcSort}
                  style={{ ...thBase, textAlign: 'center' }}
                />
              </tr>
            </thead>
            <tbody>
              {excavators.length === 0 && (
                <tr>
                  <td
                    colSpan={11}
                    style={{
                      padding: 24,
                      textAlign: 'center',
                      color: textMuted,
                    }}
                  >
                    {t('dpv2_no_excavators')}
                  </td>
                </tr>
              )}
              {sortedExcavators.map((exc) => {
                const meta = getMeta(exc);
                const isHardLocked = meta.hardlock;
                const st = lastStatuses[exc.symbotId];
                const statusRaw = st?.payload?.status ?? null;
                const isDown = statusRaw?.toLowerCase() === 'down';
                const operatorName =
                  st?.operatorName && st.operatorName.toLowerCase() !== 'system'
                    ? st.operatorName
                    : '—';
                const operatorLoggedOut = st?.operatorLoggedOut ?? false;
                const locationName =
                  (exc.nearestLoading?.name ?? '').trim() || '—';
                const nearbyBlocks =
                  gradesByLocation[locationName.trim().toLowerCase()] ?? [];
                const primaryBlock = nearbyBlocks.find(
                  (g) =>
                    g.name.trim().toLowerCase() ===
                    meta.primaryGradeBlock.trim().toLowerCase()
                );
                const gradeMaterialCategory = primaryBlock?.materialCategory
                  ?.trim()
                  .toLowerCase();
                const rowDumpOptions =
                  primaryBlock && gradeMaterialCategory
                    ? dumpOptions.filter(
                        (d) =>
                          d.locationType === 'stockpile' ||
                          d.materialCategory.some(
                            (c) =>
                              c.trim().toLowerCase() === gradeMaterialCategory
                          )
                      )
                    : dumpOptions;

                return (
                  <tr key={exc.symbotId} style={{ opacity: isDown ? 0.55 : 1 }}>
                    {/* Excavator */}
                    <td style={tdBase}>
                      <Typography
                        fontWeight={700}
                        fontSize={13}
                        sx={{ color: textPrimary }}
                      >
                        {exc.bumperNumber}
                      </Typography>
                    </td>

                    {/* Capacity */}
                    <td style={tdBase}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                        }}
                      >
                        <TextField
                          size="small"
                          value={meta.capacity}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^\d*\.?\d*$/.test(val)) {
                              setMeta(exc, 'capacity', val);
                            }
                          }}
                          disabled={isHardLocked || isDown}
                          placeholder="0"
                          inputProps={{
                            inputMode: 'decimal',
                            style: {
                              fontSize: 12,
                              padding: '5px 8px',
                              width: 70,
                            },
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': { borderRadius: 1 },
                          }}
                        />
                        <Typography
                          fontSize={11}
                          sx={{ color: textMuted, whiteSpace: 'nowrap' }}
                        >
                          t/hr
                        </Typography>
                      </Box>
                    </td>

                    {/* Operator */}
                    <td style={tdBase}>
                      {operatorName === '—' ? (
                        operatorName
                      ) : operatorLoggedOut ? (
                        <Box component="span" sx={{ color: 'error.main' }}>
                          {t('hm_operator_logged_out', { operatorName })}
                        </Box>
                      ) : (
                        operatorName
                      )}
                    </td>

                    {/* Status */}
                    <td style={tdBase}>
                      {(() => {
                        const badgeBg = statusRaw
                          ? statusGroups.get(statusRaw.toLowerCase())
                              ?.colorCode ?? theme.palette.action.selected
                          : 'transparent';
                        const rawKey = statusRaw?.toLowerCase() ?? '';
                        const displayName =
                          STATUS_DISPLAY_NAMES[rawKey] ??
                          statusRaw?.toUpperCase() ??
                          '';
                        // Activity/reason text is only meaningful while
                        // ready — any other status shows a dash instead.
                        const reasonText = isReadyStatus(statusRaw)
                          ? resolveReasonDisplay(
                              statusRaw,
                              st?.payload?.reason,
                              st?.payload?.description
                            )
                          : '—';
                        const label = statusRaw
                          ? [displayName, reasonText]
                              .filter(Boolean)
                              .join(' · ')
                          : t('dpv2_no_status');
                        return (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                              px: 1,
                              py: 0.4,
                              borderRadius: 1.5,
                              backgroundColor: statusRaw
                                ? badgeBg
                                : 'transparent',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                fontSize: 11,
                                fontWeight: 700,
                                color: statusRaw ? '#fff' : textMuted,
                                letterSpacing: '0.02em',
                              }}
                            >
                              {label}
                            </Box>
                            <IconButton
                              size="small"
                              onClick={() => {
                                onOpenStatusModal(exc);
                              }}
                              sx={{
                                p: 0.1,
                                color: statusRaw
                                  ? 'rgba(255,255,255,0.7)'
                                  : textMuted,
                                '&:hover': {
                                  color: statusRaw
                                    ? '#fff'
                                    : theme.palette.primary.main,
                                },
                              }}
                            >
                              <EditIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Box>
                        );
                      })()}
                    </td>

                    {/* Location */}
                    <td style={{ ...tdBase, whiteSpace: 'nowrap' }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <Typography fontSize={11.5} sx={{ color: textMuted }}>
                          📍 {locationName}
                        </Typography>
                        {exc.nearestLoading && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              onOpenMap(exc);
                            }}
                            sx={{
                              p: 0.25,
                              color: textMuted,
                              '&:hover': { color: theme.palette.primary.main },
                            }}
                          >
                            <MapIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        )}
                      </Box>
                    </td>

                    {/* Nearby Grade Blocks */}
                    <td style={tdBase}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {nearbyBlocks.length === 0 ? (
                          <Typography fontSize={11} sx={{ color: textMuted }}>
                            —
                          </Typography>
                        ) : (
                          nearbyBlocks.map((g) => (
                            <Chip
                              key={g.id}
                              label={g.name}
                              size="small"
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                height: 20,
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.12
                                ),
                                color: theme.palette.primary.main,
                              }}
                            />
                          ))
                        )}
                      </Box>
                    </td>

                    {/* Primary Grade Block */}
                    <td style={tdBase}>
                      {nearbyBlocks.length === 0 ? (
                        <Typography fontSize={11} sx={{ color: textMuted }}>
                          —
                        </Typography>
                      ) : (
                        <Autocomplete
                          size="small"
                          options={[...nearbyBlocks].sort((a, b) =>
                            a.name.localeCompare(b.name)
                          )}
                          getOptionLabel={(g) => g.name}
                          isOptionEqualToValue={(g, v) => g.id === v.id}
                          value={
                            nearbyBlocks.find(
                              (g) => g.name === meta.primaryGradeBlock
                            ) ?? null
                          }
                          onChange={(_, g) => {
                            setMeta(exc, 'primaryGradeBlock', g?.name ?? '');
                            setMeta(exc, 'dumpingZoneNames', []);
                          }}
                          disabled={isHardLocked || isDown}
                          sx={{
                            minWidth: 150,
                            maxWidth: 220,
                            '& .MuiAutocomplete-input': {
                              fontSize: 12,
                              textOverflow: 'ellipsis',
                            },
                          }}
                          componentsProps={{
                            popper: {
                              style: { width: 'max-content', minWidth: 220 },
                              placement: 'bottom-start',
                            },
                            paper: {
                              sx: {
                                whiteSpace: 'nowrap',
                                '& .MuiAutocomplete-option': {
                                  fontSize: 12,
                                  minHeight: 'auto',
                                },
                              },
                            },
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={t('dpv2_select_grade_block')}
                            />
                          )}
                        />
                      )}
                    </td>

                    {/* Material Grade */}
                    <td style={tdBase}>
                      {primaryBlock?.materialCategory ? (
                        <Typography
                          fontSize={12}
                          fontWeight={700}
                          sx={{ color: textPrimary }}
                        >
                          {primaryBlock.materialCategory}
                        </Typography>
                      ) : (
                        <Typography fontSize={11} sx={{ color: textMuted }}>
                          —
                        </Typography>
                      )}
                    </td>

                    {/* Locked Trucks */}
                    <td
                      style={{ ...tdBase, minWidth: 180, whiteSpace: 'normal' }}
                    >
                      <MultiSelectCell
                        popId={`manual-trk-${exc.symbotId}`}
                        open={truckPopOpen === `trk-${exc.symbotId}`}
                        onToggle={() => {
                          setTruckPopOpen((p) =>
                            p === `trk-${exc.symbotId}`
                              ? null
                              : `trk-${exc.symbotId}`
                          );
                        }}
                        onClose={() => {
                          setTruckPopOpen(null);
                        }}
                        selected={meta.lockedTrucks}
                        options={haulTrucks
                          .filter((tr) => {
                            const owner = truckLockOwner[tr.bumperNumber];
                            return !owner || owner === exc.symbotId;
                          })
                          .map((tr) => {
                            const truckStatus = getTruckStatusDisplay(
                              lastStatuses[tr.symbotId]?.payload?.status,
                              statusGroups,
                              theme
                            );
                            return {
                              id: tr.bumperNumber,
                              label: tr.bumperNumber,
                              sub: '',
                              statusLabel: truckStatus.label,
                              statusColor: truckStatus.color,
                            };
                          })}
                        onToggleItem={(id) => {
                          const next = meta.lockedTrucks.includes(id)
                            ? meta.lockedTrucks.filter((x) => x !== id)
                            : [...meta.lockedTrucks, id];
                          setMeta(exc, 'lockedTrucks', next);
                        }}
                        placeholder={t('dpv2_select_trucks')}
                        enableDarkTheme={enableDarkTheme}
                        borderColor={borderColor}
                        textMuted={textMuted}
                        textPrimary={textPrimary}
                        theme={theme}
                        disabled={isHardLocked || isDown}
                        hideChips
                      />
                      {(() => {
                        // split locked trucks into two groups instead of one flat
                        // chip list: trucks still following this excavator's own
                        // dump default (Shovel), and trucks whose actual current
                        // dump (from the live plan's assignments) diverges from it
                        // (Locked) — shown with their own real dump target inline,
                        // since a Locked Assignment can dump somewhere entirely
                        // different from the shovel's own config
                        const lockedBumpers = meta.lockedTrucks;
                        if (lockedBumpers.length === 0) return null;
                        const dumpZones = new Set(
                          meta.dumpingZoneNames.map((zone) =>
                            zone.trim().toLowerCase()
                          )
                        );
                        const shovelGroup: string[] = [];
                        const lockedGroup: Array<{
                          bumper: string;
                          dump: string;
                        }> = [];
                        lockedBumpers.forEach((bumper) => {
                          const assignment = activeAssignments.find(
                            (a) => a.truckBumper === bumper
                          );
                          if (
                            assignment &&
                            !dumpZones.has(
                              assignment.dumpingNode.trim().toLowerCase()
                            )
                          ) {
                            lockedGroup.push({
                              bumper,
                              dump: assignment.dumpingNode,
                            });
                          } else {
                            shovelGroup.push(bumper);
                          }
                        });
                        const removeTruck = (bumper: string) => {
                          setMeta(
                            exc,
                            'lockedTrucks',
                            meta.lockedTrucks.filter((x) => x !== bumper)
                          );
                        };
                        const groupLabelSx = {
                          fontSize: 9,
                          textTransform: 'uppercase' as const,
                          letterSpacing: '0.04em',
                          mb: 0.25,
                        };
                        return (
                          <Box
                            sx={{
                              mt: 0.5,
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.75,
                            }}
                          >
                            {shovelGroup.length > 0 && (
                              <Box>
                                <Typography
                                  sx={{ ...groupLabelSx, color: textMuted }}
                                >
                                  {t('dpv2_shovel_group_label')}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  {shovelGroup.map((bumper) => (
                                    <Chip
                                      key={bumper}
                                      label={bumper}
                                      size="small"
                                      disabled={isHardLocked || isDown}
                                      onDelete={
                                        isHardLocked || isDown
                                          ? undefined
                                          : () => {
                                              removeTruck(bumper);
                                            }
                                      }
                                      sx={{
                                        fontSize: 10,
                                        height: 20,
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.14
                                        ),
                                        color: theme.palette.primary.main,
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {lockedGroup.length > 0 && (
                              <Box>
                                <Typography
                                  sx={{
                                    ...groupLabelSx,
                                    color: theme.palette.warning.main,
                                  }}
                                >
                                  {t('dpv2_locked_group_label')}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    gap: 0.5,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  {lockedGroup.map(({ bumper, dump }) => (
                                    <Chip
                                      key={bumper}
                                      label={`${bumper} → ${dump}`}
                                      size="small"
                                      disabled={isHardLocked || isDown}
                                      onDelete={
                                        isHardLocked || isDown
                                          ? undefined
                                          : () => {
                                              removeTruck(bumper);
                                            }
                                      }
                                      sx={{
                                        fontSize: 10,
                                        height: 20,
                                        bgcolor: alpha(
                                          theme.palette.warning.main,
                                          0.14
                                        ),
                                        color: theme.palette.warning.main,
                                      }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        );
                      })()}
                      {/* "reassigned elsewhere" warning — commented out per user
                          request (2026-07-20), not deleted. Cross-excavator
                          reassignment is now self-healed server-side by the
                          batch PATCH itself (Backend Updates #8/#9) — no
                          client-side resolution logic to worry about breaking.
                          Re-enabling this needs its own activeAssignments
                          state back (removed as dead code when the resolution
                          logic above was deleted) — refetch it in
                          rehydrateFromActivePlan/the poll effect above.
                      {(() => {
                        const diverged = meta.lockedTrucks.filter((bumper) => {
                          const assignment = activeAssignments.find(
                            (a) => a.truckBumper === bumper
                          );
                          if (!assignment) return false;
                          return (
                            assignment.excavatorBumper !== exc.bumperNumber ||
                            assignment.overrideNode != null
                          );
                        });
                        if (diverged.length === 0) return null;
                        return (
                          <Typography
                            fontSize={10}
                            sx={{ color: theme.palette.warning.main, mt: 0.5 }}
                          >
                            {t('manual_dispatch_diverged_warning', {
                              trucks: diverged.join(', '),
                            })}
                          </Typography>
                        );
                      })()}
                      */}
                    </td>

                    {/* Dumping Location — multi-select (Backend Update #6): an
                        excavator can have multiple dump zones, and locked trucks
                        get round-robined across them by the backend */}
                    <td
                      style={{ ...tdBase, minWidth: 180, whiteSpace: 'normal' }}
                    >
                      <MultiSelectCell
                        popId={`manual-dmp-${exc.symbotId}`}
                        open={dumpPopOpen === `dmp-${exc.symbotId}`}
                        onToggle={() => {
                          setDumpPopOpen((p) =>
                            p === `dmp-${exc.symbotId}`
                              ? null
                              : `dmp-${exc.symbotId}`
                          );
                        }}
                        onClose={() => {
                          setDumpPopOpen(null);
                        }}
                        selected={meta.dumpingZoneNames}
                        options={rowDumpOptions.map((d) => ({
                          id: d.name,
                          label: d.name,
                          sub: '',
                        }))}
                        onToggleItem={(id) => {
                          const next = meta.dumpingZoneNames.includes(id)
                            ? meta.dumpingZoneNames.filter((x) => x !== id)
                            : [...meta.dumpingZoneNames, id];
                          setMeta(exc, 'dumpingZoneNames', next);
                        }}
                        placeholder={t('dpv2_select_dumps')}
                        enableDarkTheme={enableDarkTheme}
                        borderColor={borderColor}
                        textMuted={textMuted}
                        textPrimary={textPrimary}
                        theme={theme}
                        disabled={isHardLocked || isDown}
                      />
                    </td>

                    {/* Hard Lock — dispatcher can pre-lock a row's config before a
                        plan exists; the seeded value ignores any stale hardlock left
                        on the asset from a previous plan when there's no active plan
                        right now (see getMeta's fallback). Like every other field,
                        toggling it only edits local state — it's persisted to the
                        asset's meta when the dispatcher clicks "Save Config" */}
                    <td style={{ ...tdBase, textAlign: 'center' }}>
                      <Checkbox
                        checked={meta.hardlock}
                        onChange={(_, checked) => {
                          setMeta(exc, 'hardlock', checked);
                        }}
                        disabled={isDown}
                        size="small"
                        sx={{ p: 0.5 }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>
      </Box>

      {/* AUTO PLAN disabled for now — manual mode stays fully manual, no solver
          calls. Kept commented out (not deleted) in case auto-allocation comes
          back later.
      <Dialog
        open={Boolean(allocationModal)}
        onClose={() => {
          setAllocationModal(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('manual_dispatch_allocation_title')}
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {allocationModal?.rows.map((row, idx) => (
            <Box
              key={row.exc.symbotId}
              sx={{
                border: `1px solid ${borderColor}`,
                borderRadius: 1,
                p: 1.5,
              }}
            >
              <Typography fontSize={13} sx={{ mb: 1 }}>
                {t('manual_dispatch_allocation_message', {
                  bumper: row.exc.bumperNumber,
                })}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button
                  size="small"
                  variant={row.choice === 'auto' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setAllocationModal((prev) =>
                      prev
                        ? {
                            ...prev,
                            rows: prev.rows.map((r, i) =>
                              i === idx ? { ...r, choice: 'auto' } : r
                            ),
                          }
                        : prev
                    );
                  }}
                >
                  {t('manual_dispatch_allocation_auto')}
                </Button>
                <Button
                  size="small"
                  variant={row.choice === 'save' ? 'contained' : 'outlined'}
                  onClick={() => {
                    setAllocationModal((prev) =>
                      prev
                        ? {
                            ...prev,
                            rows: prev.rows.map((r, i) =>
                              i === idx ? { ...r, choice: 'save' } : r
                            ),
                          }
                        : prev
                    );
                  }}
                >
                  {t('manual_dispatch_allocation_save_only')}
                </Button>
                {row.choice === 'auto' && (
                  <TextField
                    size="small"
                    value={row.capacity}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*\.?\d*$/.test(val)) {
                        setAllocationModal((prev) =>
                          prev
                            ? {
                                ...prev,
                                rows: prev.rows.map((r, i) =>
                                  i === idx ? { ...r, capacity: val } : r
                                ),
                              }
                            : prev
                        );
                      }
                    }}
                    placeholder={t('manual_dispatch_col_capacity')}
                    inputProps={{
                      inputMode: 'decimal',
                      style: { fontSize: 12, width: 70 },
                    }}
                  />
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setAllocationModal(null);
            }}
          >
            {t('manual_dispatch_allocation_cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={
              !allocationModal?.rows.every(
                (r) =>
                  r.choice !== 'pending' &&
                  (r.choice === 'save' || Number(r.capacity) > 0)
              )
            }
            onClick={() => {
              if (!allocationModal) return;
              const resolved: Record<
                string,
                { needsAllocation: boolean; capacity: string }
              > = {};
              allocationModal.rows.forEach((r) => {
                resolved[r.exc.symbotId] = {
                  needsAllocation: r.choice === 'auto',
                  capacity: r.capacity,
                };
              });
              const { mode, allExcavators } = allocationModal;
              setAllocationModal(null);
              if (mode === 'full') {
                void submitFullPlan(resolved);
              } else {
                void submitDirtyExcavators(allExcavators, resolved);
              }
            }}
          >
            {t('manual_dispatch_allocation_confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      */}

      <Dialog
        open={pendingNavHref !== null}
        onClose={() => {
          setPendingNavHref(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('manual_dispatch_unsaved_nav_title')}
        </DialogTitle>
        <DialogContent>
          <Typography fontSize={13}>
            {t('manual_dispatch_unsaved_nav_message')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingNavHref(null);
            }}
          >
            {t('manual_dispatch_unsaved_nav_stay')}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const href = pendingNavHref;
              setPendingNavHref(null);
              setManualDirty({});
              if (href) navigate(href);
            }}
          >
            {t('manual_dispatch_unsaved_nav_leave')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManualExcavatorPanel;
